import React, { useState, useContext, useEffect } from 'react';
import { ICON_SIZE, SignInIcon } from "./actionIcons";
import fields from './fields';
import { GlobalContext } from "./GlobalContext";
import { setToken } from './csrf.js';
import { apiFetch } from './api.js';
import { Divider, TextInput } from '@tremor/react';
import { Fingerprint } from 'lucide-react';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import FullScreenScroll from './FullScreenScroll';
import { reportClientError } from './errorReporting.js';

//A network failure, by message rather than by type: fetch rejects with a bare
//TypeError, which is also what navigator.credentials.get() throws on malformed
//options, so the name alone cannot tell the two apart.
const NETWORK_ERROR = /failed to fetch|networkerror|load failed/i;

/**
 * What to tell the user when the passkey ceremony threw.
 *
 * The split that matters is whether the cause is LOCAL. A wrong domain, an
 * unreachable server or an authenticator that could not sign are all facts
 * about this browser and this page — naming them reveals nothing about who has
 * a passkey, so there is no reason to be vague. Only /passkey/auth/verify's
 * refusal has to stay a single undifferentiated string, and that path never
 * reaches here.
 *
 * Errors are matched on `name`, not `code`: @simplewebauthn/browser wraps the
 * spec's eight DOMExceptions in a WebAuthnError whose `name` it copies from
 * `cause.name` (helpers/webAuthnError.js), but it also returns some of them
 * unwrapped with no `code` at all (identifyAuthenticationError.js), so `code`
 * is present for only about half of these.
 */
const passkeyFailureMessage = (err) => {
    switch (err?.name) {
        //ERROR_INVALID_RP_ID or ERROR_INVALID_DOMAIN. The credential is bound
        //to one host — app.autodealerug.com, scoped there on purpose so tenant
        //shopfronts can never ask for a dealer's passkey (node/config.js) — and
        //the browser refuses to offer it anywhere else. Deterministic: every
        //passkey attempt from the wrong address fails this way, for everyone.
        case 'SecurityError':
            return 'Passkeys only work at this app\'s usual web address. Check the address and try again.';
        //The authenticator itself could not produce an assertion, or refused
        //the options we sent.
        case 'UnknownError':
        case 'ConstraintError':
        case 'NotSupportedError':
            return 'Your device could not complete the passkey check. Use your password instead.';
        //The server answered, but not with JSON — an nginx 502/504 page reaching
        //.json() lands here. Worth separating: it is temporary and retrying is
        //the right move, where the generic message sends them to their password.
        case 'SyntaxError':
            return 'The server sent an unexpected response. Please try again in a moment.';
        default:
            if (NETWORK_ERROR.test(err?.message || '')) {
                return 'Could not reach the server. Check your internet and try again.';
            }
            return 'Passkey sign-in failed. Use your password instead.';
    }
};


/**
 * The full-screen frame both states of this component sit in, lockup and all.
 *
 * WHY IT OWNS A SCROLLBAR. `body { overflow: hidden }` (carstyles.css:13) is
 * global, so there is no document scroll to fall back on: a full-screen box
 * here either scrolls itself or its overflow is unreachable. Before
 * `overflow-y-auto`, the sign-in card was 697px against a 660px window on an
 * ordinary 768px laptop panel, and `justify-center` splits the excess across
 * BOTH edges — the logo was cut off at the top at the same moment "Reset
 * password" went off the bottom, with no way to reach either. The passkey
 * button is what tipped it over (it and its divider are exactly 100px), but the
 * card has been one row away from this the whole time, which is the reason the
 * fix is a scroll container and not a shorter card.
 *
 * The inner box is `min-h-full`, NOT `min-h-screen`. It has to be free to grow
 * past the window for the scroll container to have anything to scroll, and
 * 100vh is the wrong number twice: it ignores this box's actual height, and on
 * a phone it counts a URL bar that is currently on screen.
 *
 * `short:` (max-height: 720px, tailwind.config.js) trims the padding and the
 * lockup so the two common laptop panels still fit outright — scrolling to sign
 * in is reachable, but it should stay the short-window case rather than the
 * everyday one.
 */
function AuthScreen({ title, subtitle, children }) {
    return (
      <FullScreenScroll>
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            {/* Centred as a unit. The lockup already reads "AUTODEALER UG", so
                it stands in for the welcome line that used to sit here. */}
            <div className="text-center">
              <img
                src="/logo-full.svg"
                alt="Auto Dealer UG"
                width="1600"
                height="1486"
                className="mx-auto w-52 short:w-36 h-auto"
              />
              <h3 className="mt-6 short:mt-3 text-tremor-title font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
                {title}
              </h3>
              <p className="mt-2 text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                {subtitle}
              </p>
            </div>
            {children}
          </div>
      </FullScreenScroll>
    );
}

function LogIn({ setLogInStatus, setUserData }){
    const navigate = useNavigate();
    const { toggleMessageBox } = useContext(GlobalContext);
    const [creds, setCreds] = useState({
        EmailAddress: '',
        Password: ''
    });
    const [errors, setErrors] = useState({
        EmailAddress: '',
        Password: ''
    });
    const [touchedFields, setTouchedFields] = useState({});
    //Off by default, deliberately: a long-lived session is a choice the user
    //makes, not one made for them on a phone that may be shared.
    const [rememberMe, setRememberMe] = useState(false);
    const [passkeySupported, setPasskeySupported] = useState(false);
    const [passkeyBusy, setPasskeyBusy] = useState(false);
    const [passkeyError, setPasskeyError] = useState('');
    //The WebAuthnError code (or the error name when there is none), shown under
    //the message so a user on the phone can read it out. The message alone is
    //for them; this is the part that is for us.
    const [passkeyReason, setPasskeyReason] = useState('');
    //Set when the credential belongs to more than one dealership. The password
    //(or passkey) has already been accepted at this point — what is missing is
    //which of them was meant. Null for everyone with a single membership, which
    //is almost everyone, and that path is untouched.
    const [dealershipChoices, setDealershipChoices] = useState(null);
    const [choosing, setChoosing] = useState(false);

    useEffect(() => {
        setPasskeySupported(browserSupportsWebAuthn());
    }, []);

    const handleChange = ({ target }) => {
        setCreds(prev => ({
            ...prev, [target.name]: target.value
        }));
        setTouchedFields(prev => ({ ...prev, [target.name]: true }));
    };

    const validateSingleInput = (name, value) => {
        let errorMessage = "";
        
        // Check if the field is empty
        if (!value) {
            errorMessage = `${fields[name].displayName || name} can't be empty`;
        } else {
            // Validate with regex if it's the EmailAddress field
            if (name === "EmailAddress") {
                const regex = new RegExp(fields.EmailAddress.regex);
                if (!regex.test(value)) {
                    errorMessage = "Invalid Email Address";
                }
            }
        }
        
        return errorMessage;
    };

    const validateAllInputs = () => {
        const errors = {
            EmailAddress: validateSingleInput("EmailAddress", creds.EmailAddress),
            Password: validateSingleInput("Password", creds.Password)
        };
        
        setErrors(errors);
        return !errors.EmailAddress && !errors.Password;
    };

    //Everything that has to happen on a successful sign-in, in one place.
    //Both the password and the passkey path end here — duplicating it would
    //mean duplicating the Preferences JSON.parse loop, which is exactly the
    //sort of thing that ends up subtly different in one of the two copies.
    const handleLoginSuccess = (resObj) => {
        setToken(resObj.csrfToken);
        setLogInStatus(true);
        navigate("/", { replace: true });
        //Preferences arrive as a map of JSON strings, one per sheet.
        const prefs = resObj.userData.Preferences || {};
        for (let sheetname of Object.keys(prefs)) {
            prefs[sheetname] = JSON.parse(prefs[sheetname]);
        }
        resObj.userData.Preferences = prefs;
        setUserData(resObj.userData);
    };

    //Both sign-in paths funnel through here so neither has to know whether a
    //second step is coming. `needsDealership` means the credential was accepted
    //but belongs to several dealerships; the session is deliberately NOT signed
    //in yet, so there is nothing to navigate to until one is picked.
    const handleAuthResponse = (resObj) => {
        if (resObj.needsDealership) {
            setToken(resObj.csrfToken);
            setDealershipChoices(resObj.choices || []);
            return;
        }
        handleLoginSuccess(resObj);
    };

    const chooseDealership = async (UserID) => {
        setChoosing(true);
        try {
            const res = await apiFetch('/login/select-dealership', { body: { UserID } });
            const resObj = await res.json();
            if (!res.ok || resObj.ok !== true) {
                toggleMessageBox({
                    on: true,
                    title: "Unable to continue",
                    message: resObj?.message || 'That dealership could not be opened.',
                    buttons: ["OK"],
                    clicked: ""
                });
                //Back to the form: the pending sign-in on the server is only
                //useful while this list is on screen, and leaving a dead list up
                //invites the same click again.
                if (res.status === 401) setDealershipChoices(null);
                return;
            }
            handleLoginSuccess(resObj);
        } catch (err) {
            toggleMessageBox({
                on: true,
                title: "Unable to continue",
                message: 'Unable to connect to the server: Please check your internet and try again.',
                buttons: ["OK"],
                clicked: ""
            });
        } finally {
            setChoosing(false);
        }
    };

    const handlePasskeyLogin = async () => {
        setPasskeyError('');
        setPasskeyReason('');
        setPasskeyBusy(true);
        //Set inside the try, consumed AFTER it. handleAuthResponse deliberately
        //does not run in there: by the time /passkey/auth/verify answers ok the
        //server has already regenerated the session and set the cookie, so the
        //user IS signed in, and a throw out of the success path (a malformed
        //Preferences blob in handleLoginSuccess, say) used to surface as "your
        //passkey failed". That is the one failure this screen's own advice
        //cannot fix, because the password path funnels through the same
        //function — "use your password instead" reproduced it exactly, where
        //handleSubmit's catch then mislabelled it a network error.
        let authed = null;
        try {
            const optRes = await apiFetch('/passkey/auth/options', { body: {} });
            const optObj = await optRes.json();
            if (!optRes.ok || !optObj.ok) {
                setPasskeyError(optObj?.message || 'Passkey sign-in is unavailable.');
                return;
            }

            //Opens the OS prompt. No email was typed and none is needed: the
            //credentials are discoverable, so the authenticator already knows
            //which accounts it holds for this site and offers them itself.
            const assertion = await startAuthentication({ optionsJSON: optObj.options });

            const verRes = await apiFetch('/passkey/auth/verify', {
                body: { response: assertion, RememberMe: rememberMe },
            });
            const resObj = await verRes.json();
            if (!verRes.ok || resObj.ok !== true) {
                //One string for every reason it refused, by design — the server
                //logs which check failed, where it is not an enumeration
                //oracle. See node/routes/passkeyAuth.js.
                setPasskeyError(resObj?.message || 'That passkey could not be verified.');
                return;
            }
            authed = resObj;
        } catch (err) {
            //NotAllowedError covers BOTH "the user cancelled" and "the prompt
            //timed out" — the spec refuses to distinguish them, precisely so a
            //site cannot work out whether someone has a passkey at all. Since
            //we cannot know it was not a deliberate cancel, saying anything
            //would be noise on the far more likely reading.
            if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return;
            //A get() is already outstanding (the autofill path). Not an error.
            if (err?.name === 'InvalidStateError') return;

            setPasskeyError(passkeyFailureMessage(err));
            //The code is shown, logged AND reported because this screen is the
            //one place in the app where an error otherwise vanishes: there is
            //no session yet, so reportClientError's own getToken() guard used
            //to drop it and /client-error 401s. Without these three a report of
            //"passkey sign-in failed" is unanswerable — nothing anywhere
            //records which of the eight causes it was.
            const code = err?.code || err?.name || 'UNKNOWN';
            setPasskeyReason(code);
            console.warn('[passkey] sign-in failed:', code, err);
            reportClientError(err, {
                kind: 'passkey-signin', label: code, signedOut: true,
            });
        } finally {
            setPasskeyBusy(false);
        }
        if (!authed) return;
        try {
            handleAuthResponse(authed);
        } catch (err) {
            //Signed in, and the app still did not start. Caught and named rather
            //than left to reject unhandled: it is reported either way (setToken
            //has already run, so the authenticated path is open), but a floating
            //rejection arrives as an anonymous stack with no indication that the
            //user is actually through the door and stuck on a blank screen.
            //
            //Not a passkey failure and deliberately not worded as one. This is
            //best-effort text — handleLoginSuccess navigates before it can
            //throw, so this screen may already be gone, which is exactly why
            //the report matters more than the message.
            console.error('[passkey] signed in, but the app failed to start:', err);
            reportClientError(err, { kind: 'post-signin-render', label: 'passkey' });
            setPasskeyError('You are signed in, but the app could not finish loading. Please reload the page.');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
            // Set all fields as touched for final validation on submit
        setTouchedFields({
          EmailAddress: true,
          Password: true
        });

        if (!validateAllInputs()) {
            console.log("Validation failed on submit.");
            return;
        }

        // Submit. If Ok, open CarsNav
        apiFetch('/login', { body: { ...creds, RememberMe: rememberMe } }).then(async (response)=>{
            const resObj = await response.json();
            console.log(`Cookie in login: ${document.cookie}`);
            console.log(`Cookie in header: ${response.headers.get("Set-Cookie")}`);
            if(!response.ok){
                if(response.status === 401){ //Wrong emailaddress or password
                    setErrors({
                      EmailAddress: resObj?.message || "emailaddress or password is wrong",
                      Password: resObj?.message || "emailaddress or password is wrong"
                    });
                    return;
                }
                //Some kind of db error
                toggleMessageBox({
                    on: true,
                    title: "Database error! Unable to login",
                    message: "Server error: "+resObj.message,
                    buttons: ["OK"],
                    clicked: ""
                });
            } else {
                if(resObj.ok!==true){ //Wrong emailaddress or password (legacy 200 shape)
                    setErrors({
                      EmailAddress: resObj?.message || "emailaddress or password is wrong",
                      Password: resObj?.message || "emailaddress or password is wrong"
                    });
                    return;
                } else { //Success — or a credential that spans several dealerships
                    handleAuthResponse(resObj);
                }
            }
        }).catch((err)=>{
            //Network error or something
            console.log(err);
            toggleMessageBox({
                on: true,
                title: "Unable To Login",
                message: "Unable to connect to the server: Please check your internet and try again.",
                buttons: ["OK"],
                clicked: ""
            });
        });
    };

    const handleBlur = ({ target }) => {
        const { name, value } = target;
        const errorMessage = validateSingleInput(name, value);
        setErrors(prev => ({ ...prev, [name]: errorMessage }));
    };

    //The credential was accepted; only "which dealership" is left. Rendered
    //instead of the form rather than over it, so there is no password box on
    //screen inviting someone to type it again — it has already been checked, and
    //asking twice would suggest the first attempt had failed.
    if (dealershipChoices) {
      return (
        <AuthScreen title="Choose a dealership" subtitle="Your account works at more than one.">
            {/* A dealership per row and no cap on how many: this is the one
                state whose height is set by the data rather than the design,
                so it is the state that most needs the frame to scroll. */}
            <div className="mt-8 flex flex-col gap-2">
              {dealershipChoices.map((c) => (
                <button
                  key={c.UserID}
                  type="button"
                  disabled={choosing}
                  onClick={() => chooseDealership(c.UserID)}
                  className="btn-t flex w-full items-center gap-3 px-4 py-3 text-left disabled:opacity-60"
                >
                  {c.LogoURL
                    ? <img src={c.LogoURL} alt="" className="h-8 w-8 shrink-0 rounded object-contain" />
                    : <span className="h-8 w-8 shrink-0 rounded bg-gray-200" />}
                  <span className="truncate font-medium">{c.CompanyName || 'Unnamed dealership'}</span>
                </button>
              ))}
            </div>

            <button
              type="button"
              className="link-t mt-6 w-full text-center underline"
              onClick={() => setDealershipChoices(null)}
              disabled={choosing}
            >
              Sign in as someone else
            </button>
        </AuthScreen>
      );
    }

    return (
      <AuthScreen
        title="Sign in to your account"
        subtitle={<>
          Don't have an account?{' '}
          <button
            className="link-t underline"
            onClick={() => {
              console.log("Coming soon!");
            navigate("/request-invite");
            }}
          >
            Request an invite
          </button>
        </>}
      >
        <form action="#" method="post" className="mt-6 space-y-4">
        <div>
          <label
          htmlFor="EmailAddress"
          className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong"
          >
          Email Address
          {errors['EmailAddress'] && <p className='input-errors'>{errors['EmailAddress']}</p>}
          </label>
          <TextInput
          type="email"
          id="EmailAddress"
          name="EmailAddress"
          placeholder="sarahconnor21@sky.net"
          className="mt-2"
          value={creds.EmailAddress} 
          onChange={handleChange} 
          onBlur={handleBlur}
          />
        </div>
        <div>
          <label
          htmlFor="Password"
          className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong"
          >
          Password
          {errors['Password'] && <p className='input-errors'>{errors['Password']}</p>}
          </label>
          <TextInput
          type="password"
          id="Password"
          name="Password"
          autoComplete="password"
          placeholder="Password"
          className="mt-2"
          value={creds.Password} 
          onChange={handleChange} 
          onBlur={handleBlur}
          />
        </div>
        {/* Wording avoids naming a duration on purpose — not because the 30
            days can't be honoured (they can, on iOS too: app. and api. are the
            same registrable domain, so ITP treats the cookie as first-party),
            but because a number in the copy is a promise, and a user who clears
            their cookies has broken it for us. */}
        <label className="mt-4 flex items-center gap-2 cursor-pointer text-tremor-default text-tremor-content dark:text-dark-tremor-content">
          <input
          type="checkbox"
          id="RememberMe"
          name="RememberMe"
          className="size-4 accent-[var(--accent-9)] cursor-pointer"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          />
          Keep me signed in on this device
        </label>
        <button
          className="btn-t btn-t-primary mt-4 w-full"
          onClick={handleSubmit}
        ><SignInIcon size={ICON_SIZE} />
          Sign in
        </button>
        </form>

        {/* The explicit button is the primary affordance, not conditional UI
            (passkeys offered inside the email field's autofill dropdown):
            autofill is invisible until focused, carries no label, and is
            unevenly implemented — a poor thing to make the only route in. */}
        {passkeySupported && (
          <>
          {/* my-6 → my-3 on a short window. The divider and this button are
              exactly 100px together, which is what turned a card that just fit
              a 768px laptop panel into one that no longer does. */}
          <Divider className="my-6 short:my-3">or</Divider>
          {passkeyError && <p className="input-errors mb-2 text-center">{passkeyError}</p>}
          {passkeyReason && (
            <p className="mb-2 text-center text-xs text-tremor-content dark:text-dark-tremor-content">
              Reason code: {passkeyReason}
            </p>
          )}
          <button
            type="button"
            className="btn-t btn-t-secondary w-full flex items-center justify-center gap-2"
            onClick={handlePasskeyLogin}
            disabled={passkeyBusy}
          >
            <Fingerprint className="size-4" aria-hidden="true" />
            {passkeyBusy ? 'Waiting for your device…' : 'Sign in with a passkey'}
          </button>
          </>
        )}
        <p className="mt-6 short:mt-3 text-tremor-default text-tremor-content dark:text-dark-tremor-content">
        Forgot your password?{' '}
        <button
          className="link-t underline"
          onClick={() => {
          navigate("/forgotpassword");
          }}
        >
          Reset password
        </button>
        </p>
      </AuthScreen>
    );
}

export default LogIn;